<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useWorkspaceSettingsStore } from '@/modules/workspace/stores/workspace-settings-store'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import WorkspaceSettingsLayout from '@/modules/workspace/components/workspace-settings-layout.vue'
import WorkspaceNav from '@/modules/workspace/components/workspace-nav.vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import {
  RouterLinkBack,
  VTextField,
  VButton,
  VButtonInvisible,
  VImageUploader,
  VToggleButtonField,
} from '@/modules/shared/components'
import GroupUrlBanner from '@/modules/workspace/components/group-url-banner.vue'
import { format } from '@/modules/shared/utils/v-table'
import VSelect from '@/modules/investing/components/v-select.vue'

const workspaceSettingsStore = useWorkspaceSettingsStore()
const authStore = useAuthStore()

const selectedTab = computed(() => 'general' as const)

const form = ref({
  name: '',
  phone: '',
  time_zone: '',
  default_currency_code: 'USD',
  setup_mode: false,
  send_registration_emails: false,
  subdomain: '',
  is_registered_investment_advisor: false,
  central_registration_depository_number: '',
  ria_verified: false,
  billing_contact_id: null,
})

const logoPreview = ref<string | null>(null)
const logoFile = ref<File | null>(null)
const replaceLogo = ref(false)

const onLogoUpload = (file: File) => {
  logoFile.value = file
  replaceLogo.value = true
}

const onLogoRemove = () => {
  logoPreview.value = null
  logoFile.value = null
  replaceLogo.value = true
}

const setForm = () => {
  const workspace = workspaceSettingsStore.workspace
  if (workspace) {
    form.value = {
      name: workspace.name ?? '',
      phone: workspace.phone ?? '',
      time_zone: workspace.time_zone ?? '',
      default_currency_code: workspace.default_currency_code ?? 'USD',
      setup_mode: workspace.setup_mode ?? false,
      send_registration_emails: false,
      subdomain: workspace.subdomain ?? '',
      is_registered_investment_advisor: workspace.is_registered_investment_advisor ?? false,
      central_registration_depository_number: workspace.central_registration_depository_number ?? '',
      ria_verified: !!workspace.registered_investment_advisor_verified_at,
      billing_contact_id: workspace.billing_contact_id,
    }
    logoPreview.value = workspace.logo_url ?? null
    logoFile.value = null
    replaceLogo.value = false
  }
}

watch(() => workspaceSettingsStore.workspace, setForm, { immediate: true })

const timezones = computed(() => workspaceSettingsStore.options?.timezones ?? [])
const currencies = computed(() => workspaceSettingsStore.options?.currencies ?? [])
const adminOptions = computed(
  () =>
    workspaceSettingsStore.options?.group_admins?.map((admin) => ({
      label: admin.name,
      value: admin.id,
    })) ?? [],
)

const submit = async () => {
  const payload: Record<string, unknown> = {
    name: form.value.name,
    phone: form.value.phone,
    time_zone: form.value.time_zone,
    default_currency_code: form.value.default_currency_code,
    setup_mode: form.value.setup_mode,
    is_registered_investment_advisor: form.value.is_registered_investment_advisor,
    central_registration_depository_number: form.value.central_registration_depository_number,
    subdomain: form.value.subdomain,
    billing_contact_id: form.value.billing_contact_id,
  }

  // Include send_registration_emails when disabling setup mode
  if (!form.value.setup_mode && workspaceSettingsStore.workspace?.setup_mode) {
    payload.send_registration_emails = form.value.send_registration_emails
  }

  if (authStore.is_site_admin) {
    payload.ria_verified = form.value.ria_verified
  }

  if (logoFile.value) {
    payload.logo = logoFile.value
  }

  if (replaceLogo.value && !logoFile.value) {
    payload.remove_logo = true
  }

  await workspaceSettingsStore.updateWorkspace(payload)
}

const sendRegistrationInvitations = async () => {
  if (confirm('Are you sure you want to send registration invitations to all unregistered investors?')) {
    await workspaceSettingsStore.sendRegistrationInvitations()
  }
}

onMounted(() => {
  workspaceSettingsStore.fetch()
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6">
      <RouterLinkBack class="text-lg" :to="{ name: 'dashboard' }">
        <VButtonInvisible>← Back</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <WorkspaceNav />

    <WorkspaceSettingsLayout :selected-tab="selectedTab">
      <form @submit.prevent="submit" class="space-y-6">
        <!-- Logo -->
        <div class="flex justify-center py-4">
          <VImageUploader
            :image="logoPreview"
            accept=".png,.jpg,.jpeg,.svg"
            label="Logo"
            @upload="onLogoUpload"
            @remove="onLogoRemove"
          />
        </div>

        <!-- Group URL Banner -->
        <GroupUrlBanner :subdomain="workspaceSettingsStore.workspace?.subdomain ?? ''" />

        <!-- Group Fields -->
        <VTextField v-model="form.name" label="Group Name" name="name" />

        <VTextField v-model="form.phone" label="Group Contact Phone Number" name="phone" type="tel" />

        <fieldset>
          <label for="time_zone" class="block text-sm font-medium text-gray-700">Group Time Zone</label>
          <select
            id="time_zone"
            v-model="form.time_zone"
            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          >
            <option v-for="tz in timezones" :key="tz.value" :value="tz.value">
              {{ tz.label }}
            </option>
          </select>
        </fieldset>

        <fieldset>
          <label for="currency" class="block text-sm font-medium text-gray-700"> Default Group Currency </label>
          <select
            id="currency"
            v-model="form.default_currency_code"
            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          >
            <option v-for="curr in currencies" :key="curr" :value="curr">
              {{ curr }}
            </option>
          </select>
        </fieldset>

        <VTextField v-if="authStore.is_site_admin" v-model="form.subdomain" label="Subdomain" name="subdomain" />

        <div class="my-8 h-px bg-gray-100"></div>

        <VSelect
          v-model="form.billing_contact_id"
          label="Stripe billing contact"
          :options="adminOptions"
          property="billing_contact_id"
        />

        <div class="my-8 h-px bg-gray-100"></div>

        <!-- Registered Investment Advisor Section -->
        <div class="text-lg font-semibold">Registered Investment Advisor</div>

        <VToggleButtonField
          v-model="form.is_registered_investment_advisor"
          label="Is your firm a Registered Investment Advisor?"
        />

        <VTextField
          v-if="form.is_registered_investment_advisor"
          v-model="form.central_registration_depository_number"
          label="Central Registration Depository (CRD) Number"
          name="central_registration_depository_number"
          placeholder="7-digit CRD number"
        />

        <div v-if="form.is_registered_investment_advisor && authStore.is_site_admin">
          <VToggleButtonField
            v-model="form.ria_verified"
            label="RIA Verification"
            description="Site admins can verify this firm."
            trueLabel="Verified"
            falseLabel="Not Verified"
          />

          <div
            v-if="workspaceSettingsStore.workspace?.registered_investment_advisor_verified_at"
            class="mt-4 rounded-md bg-green-50 p-4"
          >
            <p class="text-sm text-green-800">
              <span class="font-medium">Verified</span> on
              {{ format(workspaceSettingsStore.workspace.registered_investment_advisor_verified_at, 'date') }}
              <span v-if="workspaceSettingsStore.workspace.registered_investment_advisor_verified_by_name">
                by {{ workspaceSettingsStore.workspace.registered_investment_advisor_verified_by_name }}
              </span>
            </p>
          </div>
        </div>

        <div class="my-8 h-px bg-gray-100"></div>

        <!-- Onboarding Section -->
        <div class="text-lg font-semibold">Onboarding</div>

        <VToggleButtonField
          v-model="form.setup_mode"
          label="Setup Mode"
          description="Enabling setup mode allows admins to add investors and companies to your portfolio without automatically notifying them. Once setup mode is disabled, you have the option to send out all the notifications at once."
          trueLabel="Enabled"
          falseLabel="Disabled"
        />

        <VToggleButtonField
          v-if="!form.setup_mode && workspaceSettingsStore.workspace?.setup_mode"
          v-model="form.send_registration_emails"
          label="Send Registration Emails"
          :description="`Send registration email to investors who have not yet registered to access ${workspaceSettingsStore.workspace?.name} on Venture360?`"
          trueLabel="Yes, send emails now"
          falseLabel="No"
        />

        <!-- Button when setup mode is already disabled -->
        <fieldset
          v-if="!form.setup_mode && !workspaceSettingsStore.workspace?.setup_mode"
          class="rounded-lg border border-gray-300/50 p-4"
        >
          <div class="flex items-end justify-between gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Send Registration Invitations</label>
              <p class="mt-1 text-sm text-gray-500">
                Send registration invitations to investors who have not yet registered to access
                {{ workspaceSettingsStore.workspace?.name }} on Venture360.
              </p>
            </div>
            <VButton
              variant="v-blue"
              size="lg"
              class="shrink-0"
              :click="sendRegistrationInvitations"
              :loading="workspaceSettingsStore.loading"
            >
              Send
            </VButton>
          </div>
        </fieldset>

        <div class="pt-4">
          <VButton variant="v-blue" size="lg" type="submit" :loading="workspaceSettingsStore.loading">
            Save Changes
          </VButton>
        </div>
      </form>
    </WorkspaceSettingsLayout>
  </TheLayout>
</template>
